<template>
  <div class="event-person-list">
    <button class="close-button" v-on:click="$emit('close-person-list')"></button>
    <ul>
      <li v-for="person in personList" :key="person.email" class="event-person-list__person">
        <span class="event-person-list__person-photo">
          <img v-if="person.hasOwnProperty('thumbnailPhotoUrl') && person.thumbnailPhotoUrl" :src="person.thumbnailPhotoUrl"
               :class="{'event-person-list__person-photo--declined': person.responseStatus === 'declined'}">
          <span class="event-person-list__person-photo-substitute-initials" v-else>
            {{ personInitials(person) }}
          </span>

          <span class="event-person-list__person-response-status" :class="`event-person-list__person-response-status--${person.responseStatus}`"></span>
        </span>
        <span class="event-person-list__person-name">
          <span :class="{'event-person-list__person-name--declined': person.responseStatus === 'declined'}">{{ personName(person) }}</span>
          <span v-if="person.hasOwnProperty('organizer')" class="event-person-list__person-extra-info">
            Organiser
          </span>
          <span v-else-if="person.responseStatus === 'tentative'" class="event-person-list__person-extra-info">
            Tentative
          </span>   
          <span v-else-if="person.responseStatus === 'needsAction'" class="event-person-list__person-extra-info">
            Hasn't responded to invitation
          </span>              
          <span v-else-if="person.responseStatus === 'declined'" class="event-person-list__person-extra-info">
            Declined
            <span v-if="person.hasOwnProperty('comment')">
              - "{{ person.comment }}"
            </span>
          </span>                                  
        </span>
      </li>
    </ul>
  </div>
</template>

<script>
import StringMixins from './StringMixins.vue';

export default {
  name: 'EventPersonList',
  mixins: [StringMixins],
  props: {
    gCalApi: Object,
    event: Object
  },
  data: function() {
    return {
      availableUsers: []
    };
  },
  computed: {
    personList: function() {
      const people = this.event.attendees.filter(a => !a.hasOwnProperty('resource') || !a.resource).sort((a,b) => {
        if(a.hasOwnProperty('organizer')) {
          return -1;
        } else {
          return this.responseStatusToNumber(a.responseStatus) - this.responseStatusToNumber(b.responseStatus);
        }
      });

      people.forEach(p => {
        const matchingUsers = this.availableUsers.filter(u => u.email === p.email);

        if(matchingUsers.length > 0 && matchingUsers[0].hasOwnProperty('thumbnailPhotoUrl')) {
          p.thumbnailPhotoUrl = matchingUsers[0].thumbnailPhotoUrl;
        }
      });

      return people;
    }
  },
  mounted: function() {
    const app = this;

    this.gCalApi.client.directory.users.list({
      'domain': 'fivium.co.uk',
      'maxResults': 500,
      'viewType': 'domain_public'
    }).then(function(response) {
      app.availableUsers = response.result.users.map(u => ({
        'name': u.name.fullName, 
        'email': u.primaryEmail, 
        'thumbnailPhotoUrl': u.thumbnailPhotoUrl,
      }));
    })      
  },
  methods: {
    personName: function(person) {
      if(person.hasOwnProperty('displayName')) {
        return person.displayName;
      } else {
        return this.nameFromEmailAddress(person.email)
      }      
    },
    personInitials: function(person) {
      const name = this.personName(person);
      return name.replace( /[^A-Z]/g, '' );
    },
    responseStatusToNumber: function(responseStatus) {
      switch(responseStatus) {
        case 'accepted':
          return 10;
        case 'tentative':
          return 20;
        case 'needsAction':
          return 30;
        case 'declined':
          return 40;
      }
    }
  }
}  
</script>

<style lang="scss" scoped>
@import '../styles/components/colours';
@import '../styles/components/mixins';

.event-person-list {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: $black;
  font-size: 10em;
  overflow: auto;
  color: $white;

  &__person {
    display: flex;
    border-bottom: 1px solid rgba(255,255,255,0.2);
    padding: 0.3em 1.5em;

    &-photo {
      height: 1em;
      width: 1em;
      margin-right: 0.4em;
      border-radius: 50%;    
      background: rgba(255,255,255,0.2);  
      position: relative;

      &--declined {
        filter: grayscale(100%);
      }

      img {
        height: 100%;
        border-radius: 50%;
        vertical-align: top;
      }
    }

    &-photo-substitute-initials {
      font-size: 0.5em;
      width: 100%;
      height: 100%;
      text-align: center;
      display: block;
      line-height: 2.3em;
    }

    &-response-status {
      display: block;
      position: absolute;
      width: 0.4em;
      height: 0.4em;
      background: white;
      border-radius: 50%;
      right: -0.05em;
      bottom: -0.05em;

      &--accepted {
        background: lighten($green, 60%);

        &:after {
          content: '';
          width: 0.15em;
          height: 0.07em;
          display: block;
          position: absolute;
          top: 0.12em;
          left: 0.1em;
          border-left: 0.05em solid $green;
          border-bottom: 0.05em solid $green;
          transform: rotate(-45deg);
        }
      }

      &--declined {
        background: lighten($red, 40%);

        &:before {
          content: '';
          width: 0;
          height: 0.3em;
          display: block;
          position: absolute;
          top: 0.05em;
          left: 0.15em;
          border-left: 0.05em solid $red;
          transform: rotate(45deg);
        }

        &:after {
          content: '';
          width: 0;
          height: 0.3em;
          display: block;
          position: absolute;
          top: 0.05em;
          left: 0.15em;
          border-left: 0.05em solid $red;
          transform: rotate(-45deg);
        }        
      }      

      &--tentative {
        &:after {
          content: '?';
          font-size: 0.4em;
          color: lighten($black, 50%);
          display: block;
          position: absolute;
          font-weight: 800;
          top: 0.15em;
          left: 0.25em;
        }
      }

      &--needsAction {
        display: none;
      }
    }

    &-name {
      padding-top: 0.15em;

      &--declined {
        text-decoration: line-through;
      }
    }

    &-extra-info {
      opacity: 0.7;
      font-size: 0.5em;
      display: block;
    }
  }
}
</style>  